import { useCallback, useEffect, useState } from "react";
import api, { downloadFile, rupiah } from "@/lib/api";

const STATE_LABEL = {
  EXCEPTIONS: "ada item terbuka",
  NONE_OPEN: "tidak ada item terbuka",
  NO_SOURCES: "sumber belum disediakan — belum ada yang direkonsiliasi",
  ERROR: "gagal disusun",
};

/**
 * Every reconciliation item a domain could not clear, across domains, with the domain's own status.
 * Nothing is concluded here: each row still needs the auditor's decision on its domain screen. A domain
 * with no sources is said to have none — never shown as clean — and a failed request is a failure.
 */
export default function ExceptionInbox({ id }) {
  const [state, setState] = useState({ phase: "loading" });
  const [domain, setDomain] = useState("");

  const load = useCallback(async () => {
    setState({ phase: "loading" });
    try {
      const { data } = await api.get(`/engagements/${id}/reconciliation-exceptions`);
      // A reply without the expected lists is treated as a failure, never rendered as an empty inbox.
      if (!data || !Array.isArray(data.items) || !Array.isArray(data.groups) || !Array.isArray(data.domains)) {
        throw new Error("Unexpected reconciliation-exceptions response");
      }
      setState({ phase: "loaded", data });
    } catch (error) {
      setState({ phase: "failed", error });
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (state.phase === "loading") {
    return <div className="text-sm text-muted-foreground" data-testid="exception-inbox-loading">Memuat pengecualian rekonsiliasi...</div>;
  }
  if (state.phase === "failed") {
    return (
      <div className="text-sm border border-amber-200 bg-amber-50 text-amber-900 rounded px-3 py-2" data-testid="exception-inbox-error">
        Daftar pengecualian gagal dimuat. Ini <span className="font-semibold">bukan</span> berarti tidak ada pengecualian.
      </div>
    );
  }

  const { items, groups, domains } = state.data;
  const shown = domain ? items.filter((i) => i.domain === domain) : items;
  return (
    <div className="space-y-3" data-testid="exception-inbox">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="font-head font-semibold">Kotak Pengecualian Rekonsiliasi</h3>
          <p className="text-xs text-muted-foreground">
            Item yang belum dapat dicocokkan sistem, dengan status dari domainnya sendiri. Belum ada kesimpulan —
            setiap item tetap memerlukan keputusan auditor.
          </p>
        </div>
        <div className="flex gap-2">
          <select aria-label="Filter domain" className="border rounded h-8 px-2 text-xs bg-white" value={domain}
                  onChange={(e) => setDomain(e.target.value)} data-testid="exception-inbox-domain">
            <option value="">Semua domain</option>
            {domains.map((d) => <option key={d.domain} value={d.domain}>{d.domain}</option>)}
          </select>
          <button type="button" className="text-xs border rounded px-2 py-1 hover:bg-muted" data-testid="exception-inbox-export"
                  onClick={() => downloadFile(`/engagements/${id}/reconciliation-exceptions/export`, `pengecualian-rekonsiliasi-${id}.xlsx`)}>
            Ekspor Excel
          </button>
        </div>
      </div>
      <ul className="text-xs grid sm:grid-cols-2 lg:grid-cols-3 gap-1" data-testid="exception-inbox-domains">
        {domains.map((d) => (
          <li key={d.domain} className={d.state === "EXCEPTIONS" || d.state === "ERROR" ? "text-amber-800" : "text-muted-foreground"}
              data-testid={`exception-inbox-domain-${d.domain}`}>
            <span className="font-medium">{d.domain}</span>: {STATE_LABEL[d.state] || d.state}{d.state === "EXCEPTIONS" ? ` (${d.items})` : ""}
          </li>
        ))}
      </ul>
      {groups.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="exception-inbox-groups">
            <thead><tr className="text-left border-b"><th className="p-1">Domain</th><th className="p-1">Bagian</th><th className="p-1">Status</th><th className="p-1 text-right">Jumlah</th><th className="p-1 text-right">Nilai</th></tr></thead>
            <tbody>{groups.filter((g) => !domain || g.domain === domain).map((g) => (
              <tr key={`${g.domain}-${g.section}-${g.status}`} className="border-b last:border-0">
                <td className="p-1">{g.domain}</td><td className="p-1 break-all">{g.section}</td><td className="p-1">{g.status}</td>
                <td className="p-1 text-right tabular-nums">{g.count}</td><td className="p-1 text-right tabular-nums">{rupiah(Number(g.amount))}</td>
              </tr>))}
            </tbody>
          </table>
        </div>
      )}
      {shown.length > 0 && (
        <div className="overflow-x-auto max-h-96 overflow-y-auto">
          <table className="w-full text-xs" data-testid="exception-inbox-items">
            <thead><tr className="text-left border-b"><th className="p-1">Referensi</th><th className="p-1">Tanggal</th><th className="p-1">Status</th><th className="p-1">Pihak</th><th className="p-1 text-right">Nilai</th></tr></thead>
            <tbody>{shown.slice(0, 200).map((i, n) => (
              <tr key={n} className="border-b last:border-0">
                <td className="p-1 break-all">{i.reference ?? "—"}</td><td className="p-1 whitespace-nowrap">{i.date ?? "—"}</td>
                <td className="p-1">{i.status}</td><td className="p-1">{i.counterparty ?? "—"}</td>
                <td className="p-1 text-right tabular-nums">{i.amount == null ? "—" : rupiah(Number(i.amount))}</td>
              </tr>))}
            </tbody>
          </table>
          {shown.length > 200 && <p className="text-xs text-muted-foreground">…dan {shown.length - 200} item lainnya; lihat ekspor Excel.</p>}
        </div>
      )}
    </div>
  );
}
